import webApiClient from "./client";

/**
 * Account API Client
 *
 * Handles user account operations:
 * - Account profile retrieval
 * - Account updates
 * - User search (for admin linking workflows)
 */

export const accountApi = {
    /**
     * Search user accounts by name
     * Used by admin workflows to find and link users to bookings/classes
     * @param {string} query - Name search string
     * @returns {Promise<Array>} Array of matching user account objects
     * @throws {Error} If search fails
     */
    searchAccounts: async query => {
        const response = await webApiClient.get(
            `/api/v2/account/search?name=${encodeURIComponent(query)}`
        );

        if (!response.ok || !response.data || !response.data.users) {
            throw new Error(response.data?.message || "Failed to search accounts");
        }

        return response.data.users;
    },

    /**
     * Update a user account's profile fields
     * @param {string} accountId - Account ID to update
     * @param {object} data - Fields to update (e.g. firstName, lastName, username)
     * @returns {Promise<object>} Response data from the update
     * @throws {Error} If update fails
     */
    updateAccount: async (accountId, data) => {
        const response = await webApiClient.patch(`/api/v2/account/${accountId}`, data);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to update account");
        }

        return response.data;
    },

    /**
     * Whether the authenticated user may request account deletion, and why not if they
     * cannot (sole owner of a school with active records). Lets the UI state the outcome
     * before the user commits to the flow.
     * @returns {Promise<object>} { ok, data: { success, eligibility: { canDelete, code, message, supportEmail, blockingOrg } } }
     */
    getDeletionEligibility: async () => {
        return webApiClient.get("/api/v2/account/deletion-eligibility");
    },

    /**
     * Lodge a deletion request with support for an owner blocked by the sole-owner guard.
     * @returns {Promise<object>} { ok, data: { success, message, requestedAt, supportEmail } }
     */
    requestDeletionAssistance: async () => {
        return webApiClient.post("/api/v2/account/deletion-assistance");
    },

    /**
     * Request deletion of the authenticated user's account (30-day grace period).
     * Returns the raw { ok, data } response instead of throwing so callers can
     * branch on error codes (409 OWNER_OF_NONEMPTY_ORG carries a support message).
     * @returns {Promise<object>} { ok, data: { success, message, deletionDate, graceDays, canCancel } }
     */
    requestAccountDeletion: async () => {
        return webApiClient.delete("/api/v2/account/request-deletion");
    },

    /**
     * Get the authenticated user's own profile
     * @returns {Promise<object>} User profile object (firstName, lastName, username, etc.)
     * @throws {Error} If fetch fails or user not authenticated
     */
    getProfile: async () => {
        const response = await webApiClient.get("/api/v2/account/profile");

        if (!response.ok || !response.data || !response.data.profile) {
            throw new Error(response.data?.message || "Failed to fetch profile");
        }

        return response.data.profile;
    },
};
